import styled, {css} from 'styled-components';
import {Link} from 'react-router-dom';
import {COLORS} from 'shared/consts';

export type HiveArea =
  | 'gauge'
  | 'gaugeCards'
  | 'temp'
  | 'humid'
  | 'co2'
  | 'weight'
  | 'devices'
  | 'state'
  | 'queen'
  | 'events'
  | 'photos'
  | 'archive'
  | 'audio';

export const HiveBodySummaryWrapper = styled.div<{$withGaugeCards: boolean}>`
  display: grid;
  width: 100%;
  box-sizing: border-box;
  max-width: 2320px;

  grid-template-columns: 230px 160px repeat(3, minmax(220px, 320px)) 110px 110px minmax(60px, 1fr);
  grid-template-rows: 190px auto 222px 392px 48px;
  gap: 12px;
  height: 100%;
  padding-bottom: 5px;
  overflow-x: clip;

  grid-template-areas: ${({$withGaugeCards}) =>
    $withGaugeCards
      ? `
    'gauge gauge temp humid co2 weight weight weight'
    'gaugeCards gaugeCards state state queen audio audio audio'
    'devices devices state state photos events events events'
    'devices devices state state photos events events events'
    'archive archive archive archive archive archive archive archive'
  `
      : `
    'gauge gauge temp humid co2 weight weight weight'
    'devices devices state state queen audio audio audio'
    'devices devices state state photos events events events'
    'devices devices state state photos events events events'
    'archive archive archive archive archive archive archive archive'
  `};

  @media screen and (max-width: 1600px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    grid-template-rows: unset;
    grid-auto-rows: auto;

    grid-template-areas: ${({$withGaugeCards}) =>
      $withGaugeCards
        ? `
      'gauge gauge temp humid'
      'gaugeCards gaugeCards co2 weight'
      'devices devices state state'
      'photos photos queen queen'
      'audio audio audio audio'
      'events events events events'
      'archive archive archive archive'
    `
        : `
      'gauge gauge temp humid'
      'co2 weight state state'
      'devices devices state state'
      'photos photos queen queen'
      'audio audio audio audio'
      'events events events events'
      'archive archive archive archive'
    `};
  }

  @media screen and (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-rows: unset;
    grid-auto-rows: auto;

    grid-template-areas: ${({$withGaugeCards}) =>
      $withGaugeCards
        ? `
      'gauge gauge'
      'gaugeCards gaugeCards'
      'temp humid'
      'weight co2'
      'devices devices'
      'state state'
      'queen queen'
      'photos photos'
      'audio audio'
      'events events'
      'archive archive'
    `
        : `
      'gauge gauge'
      'temp humid'
      'weight co2'
      'devices devices'
      'state state'
      'queen queen'
      'photos photos'
      'audio audio'
      'events events'
      'archive archive'
    `};
  }

  @media screen and (max-width: 1000px) {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-bottom: 56px;
  }
`;

export const GaugeCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  min-width: 0;

  @media screen and (max-width: 1000px) {
    grid-template-columns: 1fr;
  }
`;

export const HiveAreaItem = styled.div<{$area: HiveArea}>`
  grid-area: ${({$area}) => $area};
  min-width: 0;
  ${({$area}) =>
    $area !== 'devices' && $area !== 'archive'
      ? `
        border-radius: 16px;
        background: ${COLORS.BG.SURFACE1};
      `
      : ''}

  ${({$area}) =>
    $area == 'co2' || $area == 'humid' || $area == 'temp'
      ? `
       width: 100%;
       max-width: none;
      `
      : ''}

    ${({$area}) =>
    $area == 'gauge' || $area == 'weight' || $area == 'events' || $area == 'audio'
      ? `
        min-width: 0;
      `
      : ''}
        ${({$area}) =>
    $area === 'gaugeCards'
      ? `
        background: transparent;
        border-radius: 0;
      `
      : ''}
      ${({$area}) =>
    $area == 'archive'
      ? `
        display: flex;
         @media screen and (max-width: 1000px) {
         display: flex;
         flex-direction: column;
         gap: 20px;
         }
      `
      : ''}
`;

export const HappinessArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  padding-bottom: 16px;
  min-width: 0;
`;

export const HappinessGraph = styled.div`
  position: relative;
  min-width: 0;
`;

export const HappinessGraphBG = styled.div`
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
`;

export const HappinessGraphMarker = styled.div<{$value: number}>`
  position: absolute;
  ${({$value}) => {
    const v = Math.max(0, Math.min(5, $value));
    const t = v / 5;
    const posDeg = -90 + t * 180;
    const rotDeg = -75 + t * 150;
    const rad = (posDeg * Math.PI) / 180;
    const rx = 109;
    const ry = 80;
    let left = rx * Math.sin(rad);
    if ($value >= 0.15 && $value <= 2.37) {
      left += 6;
    } else if ($value <= 4.85 && $value > 2.9) {
      left -= 6;
    }
    const top = ry * (1 - Math.cos(rad));
    return css`
      left: calc(50% + ${left}px);
      top: calc(16px + ${top}px);
      transform: translateX(-50%) rotate(${rotDeg}deg);
    `;
  }}
`;

export const HappinessValueRow = styled.div`
  margin-top: 88px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  min-height: 48px;
  min-width: 0;

  .tooltip__root {
    margin-bottom: auto;
  }

  h3 {
    color: var(--fg-default, rgba(16, 24, 40, 1));
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    font-family:
      Rostelecom Basis,
      sans-serif;
    font-size: 48px;
    font-weight: 700;
    top: 85px;
    line-height: 48px;
  }
`;

export const HappinessDesc = styled.div`
  color: var(--fg-default, rgba(16, 24, 40, 1));
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-align: center;
`;

export const HappinessUpdatedAt = styled.div`
  color: var(--fg-muted, rgba(16, 24, 40, 0.55));
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  text-align: center;
`;

export const ConnectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  border-radius: 16px;
  width: 100%;
  height: 100%;
  background: var(--bg-surface4, rgba(232, 232, 238, 1));
  padding: 16px;
  overflow: hidden;
  max-height: 428px;
  min-width: 0;

  h2 {
    color: var(--fg-default, rgba(16, 24, 40, 1));
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    max-width: 208px;
    margin-bottom: 16px;
  }

  img {
    position: absolute;
    right: -103px;
    bottom: -72px;
    width: 500px;
    height: 500px;
    z-index: 0;

    @media screen and (max-width: 1000px) {
      width: 280px;
      height: 280px;
    }
  }
`;

export const HiveBodyChartWrapper = styled.div<{$active?: boolean}>`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
  max-width: 100%;
  width: 100%;
  min-width: 0;

  ${({$active}) =>
    $active
      ? `
                    cursor: pointer;
      `
      : `
        pointer-events: none;
      `}
`;
export const HiveBodyChartTitleRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;

  h4 {
    margin-left: 4px;
    color: var(--fg-default, rgba(16, 24, 40, 1));
    font-size: 14px;
    font-weight: 500;
    line-height: 16px;
    margin-right: auto;
  }
`;

export const ArrowIconWrapper = styled.span`
  width: 24px;
  height: 24px;

  svg {
    width: 100%;
    height: 100%;
  }
`;

export const LastUpdatedWrapper = styled.p`
  color: var(--fg-muted, rgba(16, 24, 40, 0.55));
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
`;

export const HiveBodyChartAdditionalContent = styled.div`
  margin-top: 15px;
  padding-bottom: 16px;
  margin-bottom: auto;
  min-width: 0;
`;

export const HiveBodyChartNoContent = styled.div`
  margin-top: 26px;
  color: var(--fg-disabled, rgba(16, 24, 40, 0.35));
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  margin-bottom: 16px;
`;

export const HiveBodyStateWrapper = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  min-width: 0;

  > h3 {
    color: var(--fg-default, rgba(16, 24, 40, 1));
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
  }

  > h4 {
    margin-bottom: 16px;
    color: var(--fg-muted, rgba(16, 24, 40, 0.55));
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
  }
`;

export const HiveBodySeparator = styled.div`
  width: 100%;
  height: 1px;
  background: var(--border-muted, rgba(88, 93, 105, 0.15));
  margin-bottom: 16px;
`;

export const HiveBodyStateRow = styled.div`
  display: flex;
  gap: 50px;
  margin-bottom: 16px;
  min-width: 0;
`;

export const HiveBodyStateRowCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const HiveBodyStateRowColTitle = styled.h5`
  color: var(--fg-soft, rgba(16, 24, 40, 0.75));
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
`;

export const HiveBodyStateRowColValue = styled.p`
  color: var(--fg-default, rgba(16, 24, 40, 1));
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  display: flex;
  align-items: center;

  svg {
    margin-right: 4px;
  }
`;

export const HiveBodyStateSecTitle = styled.h5`
  margin-bottom: 8px;
  color: var(--fg-soft, rgba(16, 24, 40, 0.75));
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
`;

export const HiveBodyStateStates = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 16px;

  p {
    color: var(--tagitem-primary-label-color-default, rgba(16, 24, 40, 1));
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    padding: 4px 8px;
    border-radius: var(--tagitem-m-border-radius, 8px);
    background: var(--tagitem-primary-bg-color-default, rgba(88, 93, 105, 0.1));
  }
`;

export const HiveBodyStateComment = styled.p`
  color: var(--fg-default, rgba(16, 24, 40, 1));
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

export const HiveBodyUterusWrapper = styled.div`
  padding: 16px;
  position: relative;
  min-height: 222px;
  min-width: 0;
`;

export const HiveBodyUterusTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  margin-bottom: 16px;

  h3 {
    color: var(--fg-default, rgba(16, 24, 40, 1));
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
  }
`;

export const HiveBodyUterusSubtitle = styled.h4`
  color: var(--fg-soft, rgba(16, 24, 40, 0.75));
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  margin-bottom: 2px;
`;

export const HiveBodyUterusValue = styled.p`
  color: var(--fg-default, rgba(16, 24, 40, 1));
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  &:not(:last-of-type) {
    margin-bottom: 16px;
  }
`;

export const HiveBodyBeeWrapper = styled.div`
  width: 160px;
  height: 190px;
  position: absolute;
  right: 16px;
  top: 16px;
  pointer-events: none;
`;

export const HiveBodyBeeImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

export const HiveBodyMarkImage = styled.img`
  position: absolute;
  left: 50%;
  top: 64px;
  width: 22px;
  height: 22px;
  transform: translateX(-50%);
`;

export const HiveBodyMarkValue = styled.p`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 68px;
  color: var(--static-white, rgba(255, 255, 255, 1));
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
`;

export const HiveBodyPhotosContainer = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;

  img,
  div {
    width: 69px;
    height: 69px;
    border-radius: 8px;
  }

  div {
    display: flex;
    justify-content: center;
    align-items: center;
    border: 2px solid
      var(--iconbutton-outline-neutral-border-color-default, rgba(88, 93, 105, 0.15));
    background: var(--iconbutton-outline-neutral-bg-color-default, rgba(255, 255, 255, 0));
    cursor: pointer;

    svg,
    path {
      fill: var(--iconbutton-outline-neutral-icon-color-default);
    }
  }
`;

export const DevicesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: ${COLORS.BG.SURFACE1};
  border-radius: 16px;
  min-width: 0;
  height: 100%;
`;

export const DevicesTitle = styled.h3`
  color: var(--fg-default, rgba(16, 24, 40, 1));
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  padding: 16px 16px 8px;
`;

export const DevicesBlock = styled(Link)`
  display: flex;
  flex-direction: column;
  padding: 16px;
  min-width: 0;

  &:last-of-type {
    margin-bottom: 20px;
  }

  &:not(:last-of-type) {
    border-bottom: 1px solid var(--border-muted, rgba(88, 93, 105, 0.15));
  }
`;

export const DevicesBlockRowTop = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 14px;

  p {
    color: var(--fg-default, rgba(16, 24, 40, 1));
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

export const DevicesBlockRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  min-width: 0;
`;

export const DevicesBlockCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;

  h4 {
    color: var(--fg-soft, rgba(16, 24, 40, 0.75));
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
  }
`;

export const DevicesBlockColPin = styled.div<{$color: string; $bg: string}>`
  background: ${({$bg}) => $bg ?? 'var(--neutral-muted, #B5B7C0)'};
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  line-height: 16px;
  color: ${({$color}) => $color ?? 'var(--neutral-muted, #B5B7C0)'};

  svg path {
    fill: ${({$color}) => $color ?? 'var(--neutral-muted, #B5B7C0)'};
  }
`;

export const DevicesBlockRowVideos = styled.div``;
